import React, { useState, useEffect } from 'react';
import { StudyRanking } from '../../types';
import { rankingsApi } from '../../services/api';

interface Notice {
  id: string;
  title: string;
  content: string;
  created_at: string;
  priority: 'high' | 'normal' | 'low';
}

interface SeatSummary {
  total: number;
  occupied: number;
  available: number;
  occupancy_rate: number;
}

interface CombinedRanking extends StudyRanking {
  location: 'nationwide' | 'local';
  original_rank: number;
}

const KioskDisplay: React.FC = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [notices, setNotices] = useState<Notice[]>([]);
  const [nationalRankings, setNationalRankings] = useState<StudyRanking[]>([]);
  const [localRankings, setLocalRankings] = useState<StudyRanking[]>([]);
  const [combinedRankings, setCombinedRankings] = useState<CombinedRanking[]>(
    []
  );
  const [seatSummary, setSeatSummary] = useState<SeatSummary>({
    total: 100,
    occupied: 68,
    available: 32,
    occupancy_rate: 68,
  });

  // 시간 업데이트
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // 랭킹 데이터 통합 함수
  const combineRankings = (national: StudyRanking[], local: StudyRanking[]) => {
    const combined: CombinedRanking[] = [];

    // 전국 랭킹에서 상위 5명 추가
    national.slice(0, 5).forEach((ranking, index) => {
      combined.push({
        ...ranking,
        location: 'nationwide',
        original_rank: ranking.rank,
        rank: index + 1,
      });
    });

    // 로컬 랭킹에서 상위 5명 추가
    local.slice(0, 5).forEach((ranking, index) => {
      combined.push({
        ...ranking,
        location: 'local',
        original_rank: ranking.rank,
        rank: index + 6,
      });
    });

    // 순공부 시간 기준으로 재정렬
    combined.sort((a, b) => b.study_minutes - a.study_minutes);

    // 순위 재설정
    combined.forEach((item, index) => {
      item.rank = index + 1;
    });

    return combined.slice(0, 10);
  };

  // 데이터 로딩
  useEffect(() => {
    const loadData = async () => {
      try {
        // API가 구현되지 않았으므로 더미 데이터 사용

        // 전국 순공부 랭킹 더미 데이터
        const nationalDummyData = [
          {
            user_id: '1',
            name: '김철수',
            rank: 1,
            study_minutes: 480,
            study_hours: '8.0',
            grade: '고3',
            class_name: '1반',
            is_current_user: false,
          },
          {
            user_id: '2',
            name: '이영희',
            rank: 2,
            study_minutes: 450,
            study_hours: '7.5',
            grade: '고2',
            class_name: '3반',
            is_current_user: false,
          },
          {
            user_id: '3',
            name: '박민수',
            rank: 3,
            study_minutes: 420,
            study_hours: '7.0',
            grade: '고3',
            class_name: '2반',
            is_current_user: false,
          },
          {
            user_id: '4',
            name: '정소연',
            rank: 4,
            study_minutes: 390,
            study_hours: '6.5',
            grade: '고1',
            class_name: '4반',
            is_current_user: false,
          },
          {
            user_id: '5',
            name: '최동현',
            rank: 5,
            study_minutes: 360,
            study_hours: '6.0',
            grade: '고2',
            class_name: '1반',
            is_current_user: false,
          },
        ];

        // 로컬 독서실 순공부 랭킹 더미 데이터
        const localDummyData = [
          {
            user_id: '6',
            name: '한지민',
            rank: 1,
            study_minutes: 380,
            study_hours: '6.3',
            grade: '고3',
            class_name: '5반',
            is_current_user: false,
          },
          {
            user_id: '7',
            name: '송준호',
            rank: 2,
            study_minutes: 350,
            study_hours: '5.8',
            grade: '고2',
            class_name: '2반',
            is_current_user: false,
          },
          {
            user_id: '8',
            name: '류현아',
            rank: 3,
            study_minutes: 320,
            study_hours: '5.3',
            grade: '고1',
            class_name: '3반',
            is_current_user: false,
          },
          {
            user_id: '9',
            name: '장성민',
            rank: 4,
            study_minutes: 290,
            study_hours: '4.8',
            grade: '고3',
            class_name: '1반',
            is_current_user: false,
          },
          {
            user_id: '10',
            name: '윤서영',
            rank: 5,
            study_minutes: 260,
            study_hours: '4.3',
            grade: '고2',
            class_name: '4반',
            is_current_user: false,
          },
        ];

        setNationalRankings(nationalDummyData);
        setLocalRankings(localDummyData);

        // 랭킹 통합
        const combined = combineRankings(nationalDummyData, localDummyData);
        setCombinedRankings(combined);

        // 공지사항 더미 데이터 (실제로는 API에서 가져와야 함)
        setNotices([
          {
            id: '1',
            title: '신규 회원 환영 이벤트',
            content: '이번 달 신규 가입 시 첫 달 30% 할인!',
            created_at: new Date().toISOString(),
            priority: 'high',
          },
          {
            id: '2',
            title: '시설 점검 안내',
            content: '매주 일요일 오전 6-8시 시설 점검이 있습니다.',
            created_at: new Date().toISOString(),
            priority: 'normal',
          },
          {
            id: '3',
            title: '도서 기증 캠페인',
            content: '독서실에 기증할 도서를 모집합니다.',
            created_at: new Date().toISOString(),
            priority: 'normal',
          },
        ]);
      } catch (error) {
        console.error('데이터 로드 실패:', error);
      }
    };

    loadData();
    const interval = setInterval(loadData, 30000); // 30초마다 갱신

    return () => clearInterval(interval);
  }, []);

  // 자동 새로고침 (4시간마다)
  useEffect(() => {
    const refreshInterval = setInterval(
      () => {
        window.location.reload();
      },
      4 * 60 * 60 * 1000
    );
    return () => clearInterval(refreshInterval);
  }, []);

  return (
    <div
      className="h-screen w-screen bg-gradient-to-br from-slate-800 via-emerald-800 to-teal-900 overflow-hidden text-white"
      style={{
        minHeight: '100vh',
        width: '100vw',
        background:
          'linear-gradient(135deg, #1e293b 0%, #065f46 40%, #134e4a 100%)',
        overflow: 'hidden',
        color: 'white',
      }}
    >
      {/* 상단 헤더 */}
      <header
        className="bg-black bg-opacity-30 backdrop-blur-sm p-8 border-b border-white border-opacity-20"
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(8px)',
          padding: '2rem',
          borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
        }}
      >
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-6">
            <div
              className="text-6xl font-bold text-white"
              style={{
                fontSize: '3.75rem',
                fontWeight: 'bold',
                color: 'white',
              }}
            >
              📚 NewAttend 독서실
            </div>
            <div
              className="text-2xl text-blue-200"
              style={{ fontSize: '1.5rem', color: '#dbeafe' }}
            >
              학습과 성장의 공간
            </div>
          </div>

          <div className="text-right">
            <div
              className="text-4xl font-bold text-white"
              style={{
                fontSize: '2.25rem',
                fontWeight: 'bold',
                color: 'white',
              }}
            >
              {currentTime.toLocaleTimeString('ko-KR', {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </div>
            <div
              className="text-xl text-blue-200"
              style={{ fontSize: '1.25rem', color: '#dbeafe' }}
            >
              {currentTime.toLocaleDateString('ko-KR', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </div>
          </div>
        </div>
      </header>

      {/* 메인 컨텐츠 영역 */}
      <div
        className="h-[calc(100vh-160px)] grid grid-cols-12 gap-4 p-4"
        style={{
          height: 'calc(100vh - 160px)',
          display: 'grid',
          gridTemplateColumns: '1fr 2fr 1fr',
          gap: '1rem',
          padding: '1rem',
          maxWidth: '100vw',
          width: '100%',
          overflow: 'hidden',
        }}
      >
        {/* 왼쪽: 공지사항 (1/4) */}
        <div
          className="col-span-4 space-y-4"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
          }}
        >
          <NoticeBoard notices={notices} />
          <SeatStatusCard seatSummary={seatSummary} />
        </div>

        {/* 중앙: 통합 순공부 랭킹 (2/4) */}
        <div
          className="col-span-5"
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          <StudyRankingBoard rankings={combinedRankings} />
        </div>

        {/* 오른쪽: 추가 정보 (1/4) */}
        <div
          className="col-span-3 space-y-4"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
          }}
        >
          <WeatherWidget />
          <EventBanner />
          <StudyTips />
        </div>
      </div>
    </div>
  );
};

// 공지사항 보드
const NoticeBoard: React.FC<{ notices: Notice[] }> = ({ notices }) => {
  return (
    <div
      className="bg-white bg-opacity-10 backdrop-blur-lg rounded-2xl p-6 border border-white border-opacity-20"
      style={{
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(16px)',
        borderRadius: '1rem',
        padding: '1.5rem',
        border: '1px solid rgba(255, 255, 255, 0.2)',
      }}
    >
      <div
        className="flex items-center space-x-3 mb-6"
        style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '1.5rem',
        }}
      >
        <div
          className="text-4xl bg-gradient-to-r from-yellow-400 to-red-500 text-transparent bg-clip-text"
          style={{ fontSize: '2.25rem' }}
        >
          📢
        </div>
        <h2
          className="text-3xl font-bold text-white"
          style={{ fontSize: '1.875rem', fontWeight: 'bold', color: 'white' }}
        >
          공지사항
        </h2>
      </div>

      <div className="space-y-4 max-h-80 overflow-y-auto">
        {notices.map((notice) => (
          <div
            key={notice.id}
            className={`p-4 rounded-xl border-l-4 ${
              notice.priority === 'high'
                ? 'border-red-400 bg-red-500 bg-opacity-20'
                : 'border-blue-400 bg-blue-500 bg-opacity-20'
            }`}
          >
            <div
              className="font-bold text-lg text-white mb-2"
              style={{
                fontWeight: 'bold',
                fontSize: '1.125rem',
                color: 'white',
                marginBottom: '0.5rem',
              }}
            >
              {notice.title}
            </div>
            <div
              className="text-green-100 text-base leading-relaxed"
              style={{
                color: '#bbf7d0',
                fontSize: '1rem',
                lineHeight: '1.625',
              }}
            >
              {notice.content}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// 좌석 현황 카드
const SeatStatusCard: React.FC<{ seatSummary: SeatSummary }> = ({
  seatSummary,
}) => {
  return (
    <div
      className="bg-white bg-opacity-10 backdrop-blur-lg rounded-2xl p-6 border border-white border-opacity-20"
      style={{
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(16px)',
        borderRadius: '1rem',
        padding: '1.5rem',
        border: '1px solid rgba(255, 255, 255, 0.2)',
      }}
    >
      <div
        className="flex items-center space-x-3 mb-4"
        style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}
      >
        <div
          className="text-3xl bg-gradient-to-r from-green-400 to-blue-500 text-transparent bg-clip-text"
          style={{ fontSize: '1.875rem' }}
        >
          💺
        </div>
        <h3
          className="text-2xl font-bold text-white"
          style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'white' }}
        >
          좌석 현황
        </h3>
      </div>

      <div
        className="grid grid-cols-2 gap-4"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: '1rem',
        }}
      >
        <div className="text-center" style={{ textAlign: 'center' }}>
          <div
            className="text-3xl font-bold text-green-400"
            style={{
              fontSize: '1.875rem',
              fontWeight: 'bold',
              color: '#4ade80',
            }}
          >
            {seatSummary.available}
          </div>
          <div
            className="text-sm text-green-200"
            style={{ fontSize: '0.875rem', color: '#bbf7d0' }}
          >
            이용 가능
          </div>
        </div>
        <div className="text-center" style={{ textAlign: 'center' }}>
          <div
            className="text-3xl font-bold text-red-400"
            style={{
              fontSize: '1.875rem',
              fontWeight: 'bold',
              color: '#f87171',
            }}
          >
            {seatSummary.occupied}
          </div>
          <div
            className="text-sm text-blue-200"
            style={{ fontSize: '0.875rem', color: '#dbeafe' }}
          >
            이용 중
          </div>
        </div>
      </div>

      <div className="mt-4">
        <div className="flex justify-between text-sm text-blue-200 mb-2">
          <span>이용률</span>
          <span>{seatSummary.occupancy_rate}%</span>
        </div>
        <div className="w-full bg-white bg-opacity-20 rounded-full h-3">
          <div
            className="bg-gradient-to-r from-green-400 to-blue-500 h-3 rounded-full transition-all duration-500"
            style={{ width: `${seatSummary.occupancy_rate}%` }}
          ></div>
        </div>
      </div>
    </div>
  );
};

// 순공부 랭킹 보드
const StudyRankingBoard: React.FC<{ rankings: CombinedRanking[] }> = ({
  rankings,
}) => {
  return (
    <div
      className="bg-white bg-opacity-10 backdrop-blur-lg rounded-2xl p-8 border border-white border-opacity-20 h-full"
      style={{
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(16px)',
        borderRadius: '1rem',
        padding: '2rem',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        height: '100%',
      }}
    >
      <div
        className="flex items-center space-x-4 mb-8"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          marginBottom: '2rem',
        }}
      >
        <div className="text-5xl" style={{ fontSize: '3rem' }}>
          🏆
        </div>
        <div>
          <h2
            className="text-4xl font-bold text-white"
            style={{
              fontSize: '2.25rem',
              fontWeight: 'bold',
              color: 'white',
              margin: 0,
            }}
          >
            통합 순공부 랭킹
          </h2>
          <div
            className="text-lg text-green-200"
            style={{
              fontSize: '1.125rem',
              color: '#bbf7d0',
              marginTop: '0.5rem',
            }}
          >
            전국 + 우리 독서실 Weekly Rankings
          </div>
        </div>
      </div>

      <div
        className="space-y-3 overflow-y-auto"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
          maxHeight: 'calc(100vh - 320px)',
          overflowY: 'auto',
          paddingRight: '0.5rem',
        }}
      >
        {rankings.map((ranking, index) => (
          <div
            key={ranking.user_id}
            className={`flex items-center justify-between p-4 rounded-xl transition-all duration-300 ${
              index < 3
                ? 'bg-gradient-to-r from-yellow-500 from-opacity-30 to-orange-500 to-opacity-30 border border-yellow-400 border-opacity-50'
                : 'bg-white bg-opacity-5 hover:bg-opacity-10'
            }`}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '1rem',
              borderRadius: '0.75rem',
              backgroundColor:
                index < 3
                  ? 'rgba(251, 191, 36, 0.2)'
                  : 'rgba(255, 255, 255, 0.05)',
              border:
                index < 3
                  ? '1px solid rgba(251, 191, 36, 0.5)'
                  : '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <div
              className="flex items-center space-x-6"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1.5rem',
              }}
            >
              <div
                className={`text-4xl font-bold ${
                  index === 0
                    ? 'text-yellow-400'
                    : index === 1
                      ? 'text-gray-300'
                      : index === 2
                        ? 'text-orange-400'
                        : 'text-blue-200'
                }`}
                style={{
                  fontSize: '2.25rem',
                  fontWeight: 'bold',
                  color:
                    index === 0
                      ? '#fbbf24'
                      : index === 1
                        ? '#d1d5db'
                        : index === 2
                          ? '#fb923c'
                          : '#bbf7d0',
                }}
              >
                {index + 1}
              </div>

              <div
                className="flex items-center space-x-3"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                }}
              >
                <div className="text-3xl" style={{ fontSize: '1.875rem' }}>
                  {index === 0
                    ? '🥇'
                    : index === 1
                      ? '🥈'
                      : index === 2
                        ? '🥉'
                        : '👤'}
                </div>
                <div className="flex-1" style={{ flex: 1 }}>
                  <div
                    className="flex items-center space-x-2"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                    }}
                  >
                    <div
                      className="text-2xl font-bold text-white"
                      style={{
                        fontSize: '1.5rem',
                        fontWeight: 'bold',
                        color: 'white',
                      }}
                    >
                      {ranking.name}
                    </div>
                    <div
                      className={`px-2 py-1 rounded text-xs font-medium ${
                        ranking.location === 'nationwide'
                          ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                          : 'bg-gradient-to-r from-blue-500 to-green-500 text-white'
                      }`}
                      style={{
                        padding: '0.25rem 0.5rem',
                        borderRadius: '0.25rem',
                        fontSize: '0.75rem',
                        fontWeight: '500',
                        backgroundColor:
                          ranking.location === 'nationwide'
                            ? '#8b5cf6'
                            : '#3b82f6',
                        color: 'white',
                      }}
                    >
                      {ranking.location === 'nationwide'
                        ? '전국 ' + ranking.original_rank + '위'
                        : '우리독서실 ' + ranking.original_rank + '위'}
                    </div>
                  </div>
                  {ranking.grade && (
                    <div
                      className="text-sm text-green-200"
                      style={{
                        fontSize: '0.875rem',
                        color: '#bbf7d0',
                        marginTop: '0.25rem',
                      }}
                    >
                      {ranking.grade} {ranking.class_name}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="text-right" style={{ textAlign: 'right' }}>
              <div
                className="text-3xl font-bold text-white"
                style={{
                  fontSize: '1.875rem',
                  fontWeight: 'bold',
                  color: 'white',
                }}
              >
                {Math.floor(ranking.study_minutes / 60)}시간
              </div>
              <div
                className="text-lg text-green-200"
                style={{
                  fontSize: '1.125rem',
                  color: '#bbf7d0',
                }}
              >
                {ranking.study_minutes % 60}분
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// 날씨 위젯
const WeatherWidget: React.FC = () => {
  return (
    <div
      className="bg-white bg-opacity-10 backdrop-blur-lg rounded-2xl p-6 border border-white border-opacity-20"
      style={{
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(16px)',
        borderRadius: '1rem',
        padding: '1.5rem',
        border: '1px solid rgba(255, 255, 255, 0.2)',
      }}
    >
      <div className="text-center" style={{ textAlign: 'center' }}>
        <div
          className="text-5xl mb-2 bg-gradient-to-r from-yellow-300 to-orange-400 text-transparent bg-clip-text"
          style={{
            fontSize: '3rem',
            marginBottom: '0.5rem',
          }}
        >
          ☀️
        </div>
        <div
          className="text-3xl font-bold text-white"
          style={{
            fontSize: '1.875rem',
            fontWeight: 'bold',
            color: 'white',
          }}
        >
          23°C
        </div>
        <div
          className="text-sm text-green-200"
          style={{
            fontSize: '0.875rem',
            color: '#bbf7d0',
          }}
        >
          맑음
        </div>
        <div
          className="text-xs text-green-300 mt-2"
          style={{
            fontSize: '0.75rem',
            color: '#86efac',
            marginTop: '0.5rem',
          }}
        >
          공부하기 좋은 날씨입니다
        </div>
      </div>
    </div>
  );
};

// 이벤트 배너
const EventBanner: React.FC = () => {
  return (
    <div
      className="bg-gradient-to-r from-purple-500 to-pink-500 bg-opacity-90 rounded-2xl p-6 text-center"
      style={{
        background: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
        borderRadius: '1rem',
        padding: '1.5rem',
        textAlign: 'center',
      }}
    >
      <div
        className="text-3xl mb-2 bg-gradient-to-r from-purple-400 to-pink-400 text-transparent bg-clip-text"
        style={{
          fontSize: '1.875rem',
          marginBottom: '0.5rem',
        }}
      >
        🎉
      </div>
      <div
        className="text-lg font-bold text-white mb-2"
        style={{
          fontSize: '1.125rem',
          fontWeight: 'bold',
          color: 'white',
          marginBottom: '0.5rem',
        }}
      >
        이번달 특별 이벤트
      </div>
      <div
        className="text-sm text-white"
        style={{
          fontSize: '0.875rem',
          color: 'white',
        }}
      >
        신규 회원 첫 달 30% 할인!
      </div>
    </div>
  );
};

// 공부 팁
const StudyTips: React.FC = () => {
  const tips = [
    '25분 공부 + 5분 휴식 = 뽀모도로 기법',
    '새벽 시간은 집중력이 최고조!',
    '목표를 작게 나누어 달성하세요',
    '틈틈이 스트레칭으로 건강관리',
    '오늘의 목표를 명확히 정하세요',
  ];

  const [currentTipIndex, setCurrentTipIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTipIndex((prev) => (prev + 1) % tips.length);
    }, 10000); // 10초마다 변경

    return () => clearInterval(interval);
  }, [tips.length]);

  return (
    <div
      className="bg-white bg-opacity-10 backdrop-blur-lg rounded-2xl p-6 border border-white border-opacity-20"
      style={{
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(16px)',
        borderRadius: '1rem',
        padding: '1.5rem',
        border: '1px solid rgba(255, 255, 255, 0.2)',
      }}
    >
      <div className="text-center" style={{ textAlign: 'center' }}>
        <h3
          className="text-lg font-bold text-white mb-4"
          style={{
            fontSize: '1.125rem',
            fontWeight: 'bold',
            color: 'white',
            marginBottom: '1rem',
          }}
        >
          <span
            className="bg-gradient-to-r from-green-400 to-blue-400 text-transparent bg-clip-text"
            style={{
              fontSize: '1.125rem',
            }}
          >
            📚
          </span>{' '}
          Study Tip
        </h3>
        <div
          className="text-sm text-green-100 leading-relaxed"
          style={{
            fontSize: '0.875rem',
            color: '#bbf7d0',
            lineHeight: '1.625',
          }}
        >
          {tips[currentTipIndex]}
        </div>
      </div>
    </div>
  );
};

export default KioskDisplay;
